import { useCallback, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { numberLocale, useLocale, useT } from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { OptionToggle } from '@/shared/ui/OptionToggle'
import {
  cleanText,
  MAX_INPUT_CHARACTERS,
  MAX_INPUT_LINES,
  type CleanupOptions,
  type CleanupPreset,
} from '@/pages/ochistit-tekst/text-cleanup'
import {
  reportCleanupRejection,
  reportCleanupSuccess,
} from '@/pages/ochistit-tekst/text-cleanup-analytics'

const PREVIEW_LIMIT = 2_000

const DEFAULT_OPTIONS: CleanupOptions = {
  normalizeSpaces: true,
  removeEmptyLines: false,
  joinWrappedLines: false,
  tabsToSpaces: true,
  trimLines: true,
}

type CopyState = 'idle' | 'copied' | 'error'

function previewText(text: string): { value: string; clipped: boolean } {
  if (text.length <= PREVIEW_LIMIT) {
    return { value: text, clipped: false }
  }

  return { value: `${text.slice(0, PREVIEW_LIMIT)}…`, clipped: true }
}

export function TextCleanupPage() {
  const t = useT()
  const { locale } = useLocale()
  const localeCode = numberLocale(locale)
  const p = t.tools.textCleanup.page
  const [input, setInput] = useState('')
  const [options, setOptions] = useState<CleanupOptions>(DEFAULT_OPTIONS)
  const [error, setError] = useState<'character_limit' | 'line_limit' | null>(null)
  const [copyState, setCopyState] = useState<CopyState>('idle')
  const reportedInitialOperation = useRef(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const attempt = useMemo(() => cleanText(input, options), [input, options])
  const result = attempt.status === 'success' ? attempt.result : null
  const beforePreview = useMemo(() => previewText(input), [input])
  const afterPreview = useMemo(
    () => previewText(result?.output ?? ''),
    [result?.output],
  )

  const handleInput = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const startedAt = performance.now()
    const next = event.target.value
    const cleanup = cleanText(next, options)

    if (cleanup.status === 'error') {
      setError(cleanup.error)
      reportCleanupRejection(options, cleanup.error)
      return
    }

    if (!reportedInitialOperation.current && next.length > 0) {
      reportCleanupSuccess(options, cleanup.result, performance.now() - startedAt)
      reportedInitialOperation.current = true
    }

    setInput(next)
    setError(null)
    setCopyState('idle')
  }, [options])

  const togglePreset = useCallback((preset: CleanupPreset) => {
    const startedAt = performance.now()
    const nextOptions = { ...options, [preset]: !options[preset] }
    const cleanup = cleanText(input, nextOptions)
    setOptions(nextOptions)
    setCopyState('idle')
    if (input.length > 0 && cleanup.status === 'success') {
      reportCleanupSuccess(nextOptions, cleanup.result, performance.now() - startedAt)
    }
  }, [input, options])

  const handleClear = useCallback(() => {
    setInput('')
    setError(null)
    setCopyState('idle')
    reportedInitialOperation.current = false
    inputRef.current?.focus()
  }, [])

  const handleCopy = useCallback(async () => {
    if (!result?.output || !navigator.clipboard) {
      setCopyState('error')
      return
    }

    try {
      await navigator.clipboard.writeText(result.output)
      setCopyState('copied')
    } catch {
      setCopyState('error')
    }
  }, [result?.output])

  const presetOptions: Array<{
    key: CleanupPreset
    label: string
    description: string
  }> = [
    {
      key: 'normalizeSpaces',
      label: p.normalizeSpaces,
      description: p.normalizeSpacesDescription,
    },
    {
      key: 'removeEmptyLines',
      label: p.removeEmptyLines,
      description: p.removeEmptyLinesDescription,
    },
    {
      key: 'joinWrappedLines',
      label: p.joinWrappedLines,
      description: p.joinWrappedLinesDescription,
    },
    {
      key: 'tabsToSpaces',
      label: p.tabsToSpaces,
      description: p.tabsToSpacesDescription,
    },
    {
      key: 'trimLines',
      label: p.trimLines,
      description: p.trimLinesDescription,
    },
  ]

  return (
    <ToolPageShell toolKey="textCleanup">
      <div className="grid gap-4 lg:grid-cols-2 lg:gap-6">
        <div className="min-w-0 space-y-3">
          <Card className="overflow-hidden shadow-sm">
            <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-2">
              <div>
                <label htmlFor="text-cleanup-input" className="text-sm font-medium">
                  {p.sourceText}
                </label>
                <div className="text-xs text-muted-foreground">
                  {(result?.before.characters ?? 0).toLocaleString(localeCode)} {p.characters},
                  {' '}{(result?.before.lines ?? 0).toLocaleString(localeCode)} {p.lines}
                </div>
              </div>
              {input && (
                <Button variant="outline" size="sm" onClick={handleClear}>
                  {p.clear}
                </Button>
              )}
            </div>
            <textarea
              ref={inputRef}
              id="text-cleanup-input"
              value={input}
              onChange={handleInput}
              placeholder={p.inputPlaceholder}
              aria-describedby={error ? 'text-cleanup-error' : 'text-cleanup-limit'}
              className={cn(
                'block min-h-[180px] w-full resize-y border-0 bg-card p-4 font-mono text-[15px] leading-7 sm:p-5 lg:min-h-[300px]',
                'placeholder:text-muted-foreground/55 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/35',
              )}
            />
            {error ? (
              <div id="text-cleanup-error" role="alert" className="border-t bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error === 'character_limit' ? p.characterLimitError : p.lineLimitError}
              </div>
            ) : (
              <div id="text-cleanup-limit" className="border-t px-4 py-2 text-xs text-muted-foreground">
                {p.limit.replace('{characters}', MAX_INPUT_CHARACTERS.toLocaleString(localeCode))
                  .replace('{lines}', MAX_INPUT_LINES.toLocaleString(localeCode))}
              </div>
            )}
          </Card>

          <fieldset className="grid grid-cols-1 gap-2 min-[360px]:grid-cols-2">
            <legend className="sr-only">{p.presetsTitle}</legend>
            {presetOptions.map((preset) => (
              <OptionToggle
                key={preset.key}
                label={preset.label}
                description={preset.description}
                checked={options[preset.key]}
                onChange={() => togglePreset(preset.key)}
              />
            ))}
          </fieldset>
        </div>

        <Card className="flex min-w-0 flex-col overflow-hidden shadow-sm">
          <div className="flex min-h-14 flex-wrap items-center justify-between gap-3 border-b bg-muted/45 px-4 py-2">
            <div>
              <div id="text-cleanup-result-label" className="text-sm font-medium">{p.resultText}</div>
              <div className="text-xs text-muted-foreground">
                {(result?.after.characters ?? 0).toLocaleString(localeCode)} {p.characters},
                {' '}{(result?.after.lines ?? 0).toLocaleString(localeCode)} {p.lines}
              </div>
            </div>
            {result?.output && (
              <Button variant="outline" size="sm" onClick={handleCopy}>
                {copyState === 'copied' ? p.copied : copyState === 'error' ? p.copyError : p.copy}
              </Button>
            )}
          </div>
          <div
            role="region"
            aria-labelledby="text-cleanup-result-label"
            className="min-h-[180px] flex-1 overflow-auto whitespace-pre-wrap break-words bg-card p-4 font-mono text-[15px] leading-7 sm:p-5 lg:min-h-[300px]"
          >
            {result?.output || <span className="text-muted-foreground/55">{p.outputPlaceholder}</span>}
          </div>
          <p className="sr-only" aria-live="polite">
            {input && result
              ? `${result.changed ? p.changed : p.unchanged}: ${result.after.characters.toLocaleString(localeCode)} ${p.characters}, ${result.after.lines.toLocaleString(localeCode)} ${p.lines}`
              : ''}
          </p>
        </Card>
      </div>

      {input && result && (
        <>
          <Card className="mt-6 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">{p.diffTitle}</h2>
                <span className="rounded-md border bg-muted/45 px-2 py-1 text-xs text-muted-foreground">
                  {result.changed ? p.changed : p.unchanged}
                </span>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <DiffPane label={p.before} text={beforePreview.value} tone="before" />
                <DiffPane label={p.after} text={afterPreview.value} tone="after" />
              </div>
              {(beforePreview.clipped || afterPreview.clipped) && (
                <p className="mt-3 text-xs text-muted-foreground">{p.previewClipped}</p>
              )}
            </CardContent>
          </Card>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <StatCard label={p.characters} before={result.before.characters} after={result.after.characters} locale={localeCode} />
            <StatCard label={p.lines} before={result.before.lines} after={result.after.lines} locale={localeCode} />
            <StatCard label={p.emptyLines} before={result.before.emptyLines} after={result.after.emptyLines} locale={localeCode} />
          </div>
        </>
      )}

      <Card className="mt-6 border-dashed bg-card/70 shadow-sm">
        <CardContent className="p-4">
          <h2 className="text-sm font-semibold">{p.policyTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">{p.policyText}</p>
        </CardContent>
      </Card>
    </ToolPageShell>
  )
}

function DiffPane({
  label,
  text,
  tone,
}: {
  label: string
  text: string
  tone: 'before' | 'after'
}) {
  return (
    <div className={cn(
      'min-w-0 overflow-hidden rounded-lg border',
      tone === 'before'
        ? 'border-red-200 bg-red-50/70 dark:border-red-900 dark:bg-red-950/25'
        : 'border-emerald-200 bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/25',
    )}>
      <div className="border-b px-3 py-2 text-xs font-semibold">{label}</div>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words p-3 text-xs leading-5">
        {text || '∅'}
      </pre>
    </div>
  )
}

function StatCard({
  label,
  before,
  after,
  locale,
}: {
  label: string
  before: number
  after: number
  locale: string
}) {
  return (
    <Card className="shadow-sm">
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 font-mono text-lg tabular-nums">
          {before.toLocaleString(locale)} → {after.toLocaleString(locale)}
        </div>
      </CardContent>
    </Card>
  )
}
